import {Meldung} from './meldung';
import {Bereitstellung} from './bereitstellung';
import {UIAusrichtung} from './uiAusrichtung';
import {UIArtefaktTyp} from './uiArtefaktTyp';
import {ISymbol} from '@lv1871/core';
import {TextAusrichtung} from './textAusrichtung';
import {UiControl} from './uiControl';
import {EnumGruppeTechnischeSteuerung} from './enumGruppeTechnischeSteuerung';
import {EnumVotumErgebnisStop} from './enumVotumErgebnis';
import {UIDarstellungsart} from './uiDarstellungsart';
import {EnumButtonAktionsTyp} from './enumButtonAktionsTyp';

export function flatMap(array: UIArtefakt[], excludeMap?: { [key: string]: string[] }): UIArtefakt[] {
  let excludeKeys: string[];
  if (!!excludeMap) {
    excludeKeys = Object.keys(excludeMap);
  }
  return array.reduce((previousValue: UIArtefakt[], currentValue: UIArtefakt) => {
        if (!!excludeKeys && excludeMap[excludeKeys[0]].includes(currentValue[excludeKeys[0]])) {
          return previousValue;
        } else {
          previousValue.push(currentValue);
          if (!!currentValue.UIArtefakt) {
            previousValue.push(...flatMap(currentValue.UIArtefakt, excludeMap));
          }
        }
        return previousValue;
      },
      [] as UIArtefakt[]);
}

class UIArtefaktWithParent {
  artefakt: UIArtefakt;
  parent: UIArtefakt;
}

export function flatMapWithParent(array: UIArtefakt[], parent?: UIArtefakt): UIArtefaktWithParent[] {
  return array.reduce((previousValue: UIArtefaktWithParent[], currentValue: UIArtefakt) => {
        previousValue.push({artefakt: currentValue, parent});
        if (!!currentValue.UIArtefakt) {
          previousValue.push(...flatMapWithParent(currentValue.UIArtefakt, currentValue));
        }
        return previousValue;
      },
      [] as UIArtefaktWithParent[]);
}

export function hasVotumBox(uiControls: UiControl): boolean {
  return !!flatMap(uiControls.uiVorgang.UIArtefakt).filter((artefakt: UIContainerControl) => artefakt.UIContainerComponent === 'VotumBox').length;
}

export function votumBlocksWorkflow(uiControls: UiControl): boolean {
  return !!flatMap(uiControls.uiVorgang.UIArtefakt).filter(artefakt => artefakt.FeldnameExtern === '/votum/votumErgebnis' && (Object as any).values(EnumVotumErgebnisStop).includes(artefakt.WertEingabe)).length;
}

export function findSingleArtefakt(refIdent: String, identifikator: String, array: UIArtefakt[]): UIArtefakt {
  return flatMap(array).find(uiArtefakt =>
      !!(uiArtefakt as UiBaseControl).ReferenzIdentifikator
      && (uiArtefakt as UiBaseControl).ReferenzIdentifikator === refIdent
      && uiArtefakt.Identifikator === identifikator);
}

export function firstNestedSymbolControl(allArtefakts: UIArtefakt[]): ISymbol {
  if (!allArtefakts) {
    return null;
  }
  const uiArtefakt = new Array<UIArtefakt>();
  uiArtefakt.push(allArtefakts.filter(artefakt => artefakt.UIArtefakttyp === UIArtefaktTyp.SYMBOL)[0]);
  return uiArtefakt[0] as SymbolControl as ISymbol;
}

export function firstNestedActionButtonControl(allArtefakts: UIArtefakt[]): ActionButtonControl {
  if (!allArtefakts) {
    return null;
  }
  const uiArtefakt = flatMap(allArtefakts)
      .filter(artefakt => (artefakt as ActionButtonControl).UIArtefakttyp === UIArtefaktTyp.ACTION_BUTTON &&
          (artefakt as ActionButtonControl).Darstellungsart === UIDarstellungsart.AUSGEBLENDET)[0];
  return uiArtefakt as ActionButtonControl;
}


export function uniqueKey(artefakt: UIArtefakt): string {
  let key = artefakt.Identifikator;
  if (!!(artefakt as UiBaseControl).ReferenzIdentifikator) {
    key = key + (artefakt as UiBaseControl).ReferenzIdentifikator;
  }
  if (!!(artefakt as UiInputControl).ReferenzListItemKey) {
    key = key + (artefakt as UiInputControl).ReferenzListItemKey;
  }
  return key;
}

export function getHTMLControlName(artefakt: UiBaseControl): string {
  let refIndikator = '';
  refIndikator += !!artefakt.ReferenzIdentifikator ? artefakt.ReferenzIdentifikator : '';
  refIndikator += !!artefakt.ReferenzListItemKey ? artefakt.ReferenzListItemKey : '';
  refIndikator += !!artefakt.menuseiteForValidationOnly ? artefakt.menuseiteForValidationOnly : '';
  return artefakt.Identifikator + refIndikator;
}

export function hasGruppeTechnischeSteuerung(uiArtefakt: UiInputControl, enumGruppe: EnumGruppeTechnischeSteuerung): boolean {
  let technischeGruppen = [];
  if (!!uiArtefakt.GruppeTechnischeSteuerung) {
    technischeGruppen = uiArtefakt.GruppeTechnischeSteuerung.split(',').map(gruppe => gruppe.trim());
  }
  return technischeGruppen.includes(enumGruppe);
}

export function syncSameArtifacts(uiControl: UiControl, artefakt: UIArtefakt) {
  flatMap(uiControl.uiVorgang.UIArtefakt).filter(uiArtefakt =>
      uniqueKeyForArtefaktSync(
          artefakt, uiControl.uiVorgang.UIArtefakt) === uniqueKeyForArtefaktSync(uiArtefakt, uiControl.uiVorgang.UIArtefakt)
      && (artefakt.WertEingabe !== uiArtefakt.WertEingabe ||
          artefakt.AntragsabweichungJN !== uiArtefakt.AntragsabweichungJN)).forEach(uiArtefakt => {
    uiArtefakt.WertEingabe = artefakt.WertEingabe;
    uiArtefakt.AntragsabweichungJN = artefakt.AntragsabweichungJN;
    uiArtefakt.recreateComponent = true;
  });
}

export function uniqueKeyForArtefaktSync(artefakt: UIArtefakt, uiArtefakte: UIArtefakt[]) {
  if (!!artefakt['UIVorgangID'] && artefakt.UIArtefakttyp !== UIArtefaktTyp.UIVORGANG_REFERENZ) {
    const uiRef = flatMap(uiArtefakte).filter(artef => artef.UIArtefakttyp === UIArtefaktTyp.UIVORGANG_REFERENZ)
    .filter(artef => artefakt['UIVorgangID'] === artef['UIVorgangID']);
    if (uiRef.length > 0) {
      return artefakt.Identifikator + uiRef[0].Identifikator + '@' + uiRef[0].WertEingabe;
    } else {
      return artefakt.Identifikator;
    }
  } else {
    return uniqueKey(artefakt);
  }
}

export interface UIArtefaktContainer {
  UIArtefakt: UIArtefakt[];
}

export class UIArtefakt implements UIArtefaktContainer {
  Identifikator: string;
  IdentifikatorUuid: string;
  UIArtefakttyp: string;
  UIArtefakt: UIArtefakt[];
  WertEingabe: any;
  BreiteInPixel: string;
  Ausrichtung: UIAusrichtung;
  recreateComponent: boolean;
  FeldnameRisikopruefung?: string;
  FeldnameExtern?: string;
  // TB: menuseiteForValidationOnly sollte NUR im validationService genutzt werden, sonst bitte nirgendwo
  menuseiteForValidationOnly: string;
  AntragsabweichungZulaessigJN: boolean;
  AntragsabweichungJN: boolean;
  Synthetic: boolean;
}

export class UiBaseControl extends UIArtefakt {
  ReferenzIdentifikator?: string;
  UIArtefaktGruppe: string;
  InfoTextPermanent: string;
  InfoTextTooltipCollapsable: string;
  InfoTextTooltipCollapsableTyp: string;
  Darstellungsart: string;
  Text: string;
  ReferenzListItemKey: string;
}

export class UiInputControl extends UiBaseControl {
  Hinweis: string;
  PflichtfeldJN: boolean;
  FehlertextPflichtfeld: string;
  KalkulationsrelevantJN: boolean;
  UIMeldung: Meldung[];
  Tabellenzeilengestaltung: Tabellenzeilengestaltung;
  Werteliste: WertelisteElement[];
  Quellsystem: string;
  UiVorgangID?: string;
  GruppeTechnischeSteuerung: string;
  Titel: string;
  FeldnameTracking?: string;
}

export class Tabellengestaltung {
  UITabellenzeile: UITabellenzeile[];
}

export class TabelleControl extends UiInputControl {
  Tabellengestaltung: Tabellengestaltung;
  SichtbareZeilen: number;
}

class UITabellenzelle {
  Attribut: string;
  Wert: number;
}

export class UITabellenzeile {
  UITabellenzelle: UITabellenzelle[];
  Attribut: string;
  Wert: number;
}

class Tabellenzeilengestaltung {
  UITabellenzeile: UITabellenzeile[];
}

export class UIMenu extends UIArtefakt {
  Titel: string;
  UIMenuGruppen: UIMenuGruppe[];
}

export class UIMenuSeite extends UIArtefakt {
  Titel: string;
  SeitenID: string;
  MenuTitel: string;
  WeiterButtonLabel: string;
  ZurueckButtonLabel: string;
}

export class UIMenuGruppe extends UIArtefakt {
  MenuTitel: string;
  WeiterButtonLabel: string;
  ZurueckButtonLabel: string;
  UIMenuSeiten: UIMenuSeite[];
  MenuSeitenAusgeblendetJN: boolean;
}

export class UIArtefaktGruppe extends UIMenu {
  UIArtefaktGruppeComponent: string;
  UIArtefakt = new Array<UIArtefakt>();
  UIArtefaktGruppe: string;
  Hervorhebungstyp: string;
  CardLayoutUIContainer: string;
  Text: string;
  ArtefaktAnordnung: string;
}

export class ButtonControl extends UiInputControl {
  ButtonComponent: string;
  Color: string;
  Command: string;
  DownloadTyp: string;
  DokumentPaketTyp: string;
  SeitenID: string;
  RedirectUrl: string;
  HtmlSeiteExtern: string;
}

export class ActionButtonControl extends ButtonControl {
  ButtonAktionsTyp: EnumButtonAktionsTyp;
}

export class Fliesstext extends UiBaseControl {
  Titel: string;
  TextAusrichtung: TextAusrichtung;
}

export class Adresse extends UiInputControl {
  TextAusrichtung: TextAusrichtung;
  TextAdresseNichtGefunden: string;
  IstAdressKomponente: boolean;
}

export class AbsatzControl extends UiBaseControl {
  AbsatzComponent: string;
  Titel: string;
  TextAusrichtung: TextAusrichtung;
}

export class Modul extends UiBaseControl {
  ModulID: string;
}

export class NumberControl extends UiInputControl {
  NumberComponent: string;
  Einheit: string;
  WertEingabeMin: number;
  WertEingabeMax: number;
  AnzahlZeichenMaximum: number;
  AnzahlZeichenMinimum: number;
  SliderSchrittweite: number;
  SliderLabelAnzeigenJN: boolean;
}

export class DoubleControl extends UiInputControl {
  Nachkommastellen: number;
  Einheit: string;
  WertEingabeMin: number;
  WertEingabeMax: number;
}

export class BooleanControl extends UiInputControl {
  BooleanComponent: string;
  RisikofrageJN: boolean;
}

export class LesebestaetigungControl extends BooleanControl {
  LesebestaetigungComponent: string;
  Label: string;
  DokumentPaketTyp: string;
}

export class DateControl extends UiInputControl {
  DatumComponent: string;
  DatumTagVorgegeben: number;
}

export class Wertemenge extends UiInputControl {
  MultiJN: boolean;
  WertemengeComponent: string;
  EnumBereitstellung: Bereitstellung;
  WertAnzeige: string;
  WertelisteMitNullWertJN: boolean;
}

export class RisikosuchfrageControl extends Wertemenge {
  RisikosuchfrageComponent: string;
  EigeneEingabeMoeglich: boolean;
  DefaultVorgabeRisikofragenservice: string;
}

export class WertelisteElement {
  WertEingabe: string;
  risikoklasse?: string;
  beschreibung?: string;
  externerLink?: string;
  WertAnzeige: string;
  synonyme?: string[] = new Array<string>();
}

export class WertelisteElementKrankheit extends WertelisteElement {
  id: string;
  ModulID: number;
  synonyme?: string[] = new Array<string>();
}

export class TextControl extends UiInputControl {
  TextComponent: string;
  AnzahlZeichenMaximum: number;
  AnzahlZeichenMinimum: number;
  SichtbareZeilenMinimum: number;
  SichtbareZeilenMaximum: number;
}

export class IBANControl extends TextControl {
  Bankname: string;
}

export class SymbolControl extends UIArtefakt {
  Symbolbezeichnung: string;
  SymbolGroesseInPixel: string;
  SymbolFarbe: string;
  SymbolAlsPraefixJN: boolean;
}

export class UIContainerControl extends UiBaseControl {
  UIContainerComponent: string;
  CardLayoutUIContainer: string;
  Titel: string;
  ShrinkedLabel: string;
}

export class ListControl extends UiInputControl {
  ListeErweiterbarJN: boolean;
  ListenelementeMin: number;
  ListenelementeMax: number;
  template: ListItemControl;
}

export class ListItemControl extends UiBaseControl {
  ListenelementComponent: string;
  TemplateJN: boolean;
  UniqueTrackingID: string;
}

export class UiVorgangReferenzControl extends UIArtefakt {
  UIVorgangID: string;
  VorgangBereitstellung: Bereitstellung;
}
